import { useCallback, type ReactNode } from 'react';
import { Undo2, Plus, ChevronDown, ChevronRight } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useTranslation } from '@/hooks/useTranslation';
import type { RowState } from './types';

type ActionType = 'rollback' | 'delete' | 'add';

interface ActionCellProps {
  rowState?: RowState;
  isSeparator?: boolean;
  monthKey?: string;
  hasId?: boolean;
  /** Which actions to show. Defaults to all: ['rollback', 'delete', 'add']. */
  actions?: ActionType[];
  /**
   * Dropdown-menu content for the clean-row state. When non-empty (and the
   * row has an id + isn't dirty), renders a `ChevronDown` trigger that opens
   * a DropdownMenu with these items.
   *
   * `EditableTable` is the canonical builder — it composes the expand-toggle
   * item, caller items from `renderRowMenu`, and the auto-injected delete
   * item into a single fragment of `<DropdownMenuItem>`s. Dirty rows still
   * show the rollback arrow regardless.
   */
  menuContent?: ReactNode;
  /**
   * ID of the detail-row container the expand-toggle item controls. When
   * present, the chevron button sets `aria-controls={expandDetailId}` so
   * assistive tech can jump from the trigger to the panel content.
   */
  expandDetailId?: string;
  /**
   * `expandTrigger="chevron"`: a clean row with an id shows this bare
   * chevron toggle instead of the dropdown (dirty rows still roll back).
   */
  expandToggle?: { isExpanded: boolean; onToggle: () => void; label: string };
  /**
   * Identifies the row this cell belongs to. Bound to `onRowRollback`
   * internally via `useCallback` so the rollback handler is stable across
   * DataRow re-renders.
   */
  rowId?: string;
  /** Unbound parent rollback callback. Bound to `rowId` internally. */
  onRowRollback?: (rowId: string) => void;
  onAddRow?: () => void;
}

export function ActionCell({
  rowState,
  isSeparator,
  monthKey,
  hasId,
  actions,
  menuContent,
  expandDetailId,
  expandToggle,
  rowId,
  onRowRollback,
  onAddRow,
}: ActionCellProps) {
  const { t } = useTranslation('spreadsheet');

  // Bind the parent callback to (rowId) once per identity change, so dirty-row
  // re-renders inside DataRow don't hand the rollback button a fresh function
  // ref every render. Safe to call even when ActionCell ends up rendering the
  // separator/clean-row branch (the handler just won't be wired to anything).
  const handleRollback = useCallback(() => {
    if (onRowRollback && rowId !== undefined) onRowRollback(rowId);
  }, [onRowRollback, rowId]);

  const showAction = (action: ActionType) => !actions || actions.includes(action);

  // Separator row: show add button
  if (isSeparator && monthKey && onAddRow && showAction('add')) {
    return (
      <button
        className="separator-add-btn"
        onClick={onAddRow}
        title={t('actions.addRow')}
        aria-label={t('actions.addRow')}
      >
        <Plus className="w-3.5 h-3.5" />
      </button>
    );
  }

  // Modified/new/deleted: show rollback button
  if ((rowState === 'modified' || rowState === 'new' || rowState === 'deleted') && showAction('rollback')) {
    return (
      <button
        className={`rollback-btn ${rowState === 'deleted' ? 'delete-btn' : ''}`}
        onClick={handleRollback}
        title={t('actions.undoChanges')}
        aria-label={t('actions.undoChanges')}
      >
        <Undo2 className="w-3.5 h-3.5" />
      </button>
    );
  }

  // Chevron trigger: the same toggle the index-grid shell renders.
  if (hasId && expandToggle) {
    return (
      <button
        type="button"
        className="rollback-btn expand-toggle-btn"
        aria-expanded={expandToggle.isExpanded}
        aria-controls={expandDetailId}
        aria-label={expandToggle.label}
        title={expandToggle.label}
        onClick={(e) => {
          e.stopPropagation();
          expandToggle.onToggle();
        }}
      >
        <ChevronRight
          className="w-3.5 h-3.5"
          style={{ transform: expandToggle.isExpanded ? 'rotate(90deg)' : undefined, transition: 'transform 0.15s' }}
        />
      </button>
    );
  }

  // Unchanged row with id + non-empty menu items: ChevronDown opens the
  // composed dropdown (expand toggle + caller items + delete). The icon
  // matches `ui/action-menu.tsx` so the row affordance reads identically
  // across the app.
  if (hasId && menuContent) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="rollback-btn"
            title={t('actions.rowActions')}
            aria-label={t('actions.rowActions')}
            aria-controls={expandDetailId}
          >
            <ChevronDown className="w-3.5 h-3.5 stroke-[2.5]" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-48">
          {menuContent}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return null;
}
